import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';

// Contexts
import { AppProvider } from './contexts/AppContext';
import ErrorBoundary from './components/common/ErrorBoundary';

// Components
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Lazy load pages - usando las rutas correctas
const Home = React.lazy(() => import('./pages/public/Home'));
const Features = React.lazy(() => import('./pages/public/Features'));
const PublicMonitor = React.lazy(() => import('./pages/public/Monitor'));
const Contact = React.lazy(() => import('./pages/public/Contact'));
const CookiesPage = React.lazy(() => import('./pages/public/Cookies'));
const PrivacyPage = React.lazy(() => import('./pages/public/PrivacyPolicy'));
const Donations = React.lazy(() => import('./pages/public/Donations'));
const Login = React.lazy(() => import('./pages/public/Login'));
const DetailPage = React.lazy(() => import('./pages/public/DetailPage'));

// Protected pages - están en el directorio raíz
const Dashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const ProtectedMonitor = React.lazy(() => import('./pages/admin/Monitor'));

// Admin pages
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const CombosAdminList = React.lazy(() => import('./pages/admin/CombosAdminList'));
const ContactAdminList = React.lazy(() => import('./pages/admin/ContactAdminList'));
const ConfigPage = React.lazy(() => import('./pages/admin/ConfigPage'));
const DataAdmin = React.lazy(() => import('./pages/admin/DataAdmin'));

// Admin detail pages
const PingDetail = React.lazy(() => import('./pages/admin/PingDetail'));
const ContactAdminDetail = React.lazy(() => import('./pages/admin/ContactAdminDetail'));
const NetworkDiagram = React.lazy(() => import('./pages/admin/NetworkDiagram'));
const VideoMonitor = React.lazy(() => import('./pages/admin/VideoMonitor'));
const ScoreDetail = React.lazy(() => import('./pages/admin/ScoreDetail'));
const UserDetail = React.lazy(() => import('./pages/admin/UserDetail'));
const TrainingDetail = React.lazy(() => import('./pages/admin/TrainingDetail'));
const SubscriptionsAdmin = React.lazy(() => import('./pages/admin/SubscriptionsAdmin'));
const TrainingSessionsAdmin = React.lazy(() => import('./pages/admin/TrainingSessionsAdmin'));
const TrainingSessionDetail = React.lazy(() => import('./pages/admin/TrainingSessionDetail'));
const CoachLinksAdmin = React.lazy(() => import('./pages/admin/CoachLinksAdmin'));
const GoalsAdmin = React.lazy(() => import('./pages/admin/GoalsAdmin'));
const NotificationsAdmin = React.lazy(() => import('./pages/admin/NotificationsAdmin'));
const NotificationCreate = React.lazy(() => import('./pages/admin/NotificationCreate'));
const NotificationDetail = React.lazy(() => import('./pages/admin/NotificationDetail'));
const AdaptivePlanAdmin = React.lazy(() => import('./pages/admin/AdaptivePlanAdmin'));
const ProgramsAdmin = React.lazy(() => import('./pages/admin/ProgramsAdmin'));
const ProgramDetail = React.lazy(() => import('./pages/admin/ProgramDetail'));
const PlanBuilder = React.lazy(() => import('./pages/admin/PlanBuilder'));

// Users admin
const UsersAdmin = React.lazy(() => import('./pages/admin/UsersAdmin'));
const UserProfileDetail = React.lazy(() => import('./pages/admin/UserProfileDetail'));

// Email & Newsletter admin
const EmailCampaignsAdmin = React.lazy(() => import('./pages/admin/EmailCampaignsAdmin'));
const EmailCampaignCreate = React.lazy(() => import('./pages/admin/EmailCampaignCreate'));
const EmailCampaignDetail = React.lazy(() => import('./pages/admin/EmailCampaignDetail'));
const EmailTemplatePreview = React.lazy(() => import('./pages/admin/EmailTemplatePreview'));
const NewsletterEditionsAdmin = React.lazy(() => import('./pages/admin/NewsletterEditionsAdmin'));
const NewsletterEditionCreate = React.lazy(() => import('./pages/admin/NewsletterEditionCreate'));
const NewsletterEditionDetail = React.lazy(() => import('./pages/admin/NewsletterEditionDetail'));
const NewsletterSubscribersAdmin = React.lazy(() => import('./pages/admin/NewsletterSubscribersAdmin'));
const NewsletterListsAdmin = React.lazy(() => import('./pages/admin/NewsletterListsAdmin'));
const NewsletterStatsAdmin = React.lazy(() => import('./pages/admin/NewsletterStatsAdmin'));

// Company landing (Trwfa Forge)
const CompanyLayout = React.lazy(() => import('./components/company/CompanyLayout'));
const CompanyLanding = React.lazy(() => import('./pages/company/CompanyLanding'));

const AppRoutes = () => {
  return (
    <Router>
      <Routes>
        {/* Rutas públicas */}
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="features" element={<Features />} />
          <Route path="monitor" element={<PublicMonitor />} />
          <Route path="contact" element={<Contact />} />
          <Route path="cookies" element={<CookiesPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="donations" element={<Donations />} />
          <Route path="login" element={<Login />} />
          <Route path="detail/:id" element={<DetailPage />} />
        </Route>

        {/* Rutas protegidas */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
        </Route>

        <Route 
          path="/monitor-protected" 
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ProtectedMonitor />} />
        </Route>

        {/* Rutas de administración */}
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute requireAdmin={true}>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="combos" element={<CombosAdminList />} />
          <Route path="contacts" element={<ContactAdminList />} />
          <Route path="contacts/:id" element={<ContactAdminDetail />} />
          <Route path="config" element={<ConfigPage />} />
          <Route path="data" element={<DataAdmin />} />
          <Route path="ping/:id" element={<PingDetail />} />
          <Route path="score/:id" element={<ScoreDetail />} />
          <Route path="users" element={<UsersAdmin />} />
          <Route path="users/:id" element={<UserProfileDetail />} />
          <Route path="user/:id" element={<UserDetail />} />
          <Route path="trainings/:id" element={<TrainingDetail />} />
          <Route path="network" element={<NetworkDiagram />} />
          <Route path="videos" element={<VideoMonitor />} />
          <Route path="subscriptions" element={<SubscriptionsAdmin />} />
          <Route path="training-sessions" element={<TrainingSessionsAdmin />} />
          <Route path="training-sessions/:id" element={<TrainingSessionDetail />} />
          <Route path="coach-links" element={<CoachLinksAdmin />} />
          <Route path="goals" element={<GoalsAdmin />} />
          <Route path="notifications" element={<NotificationsAdmin />} />
          <Route path="notifications/new" element={<NotificationCreate />} />
          <Route path="notifications/:id" element={<NotificationDetail />} />
          <Route path="adaptive-plans" element={<AdaptivePlanAdmin />} />
          <Route path="plan-builder" element={<PlanBuilder />} />
          <Route path="programs" element={<ProgramsAdmin />} />
          <Route path="programs/:id" element={<ProgramDetail />} />
          <Route path="email" element={<EmailCampaignsAdmin />} />
          <Route path="email/new" element={<EmailCampaignCreate />} />
          <Route path="email/templates" element={<EmailTemplatePreview />} />
          <Route path="email/:id" element={<EmailCampaignDetail />} />
          <Route path="newsletter" element={<NewsletterEditionsAdmin />} />
          <Route path="newsletter/new" element={<NewsletterEditionCreate />} />
          <Route path="newsletter/subscribers" element={<NewsletterSubscribersAdmin />} />
          <Route path="newsletter/lists" element={<NewsletterListsAdmin />} />
          <Route path="newsletter/stats" element={<NewsletterStatsAdmin />} />
          <Route path="newsletter/:id" element={<NewsletterEditionDetail />} />
        </Route>

        {/* Landing Trwfa Forge */}
        <Route path="/company" element={<CompanyLayout />}>
          <Route index element={<CompanyLanding />} />
        </Route>

        {/* Redirecciones para evitar bucles */}
        <Route path="/logout" element={<Navigate to="/" replace />} />
        
        {/* 404 - Ruta por defecto */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AppProvider>
          <div className="App">
            <Suspense 
              fallback={
                <div className="min-h-screen flex items-center justify-center">
                  <LoadingSpinner size="large" message="Cargando aplicación..." />
                </div>
              }
            >
              <AppRoutes />
            </Suspense>
            
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#4aed88',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ff6b6b',
                  },
                },
              }}
            />
          </div>
        </AppProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;